import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import {
  useAgencyRefDataSource,
  useClientSearchDataSource,
  useSelectAgencyPortsDataSource,
  useSelectPortCallTypeDataSource,
  useVesselSearchDataSource
} from '@gat-ship/ref-data'
import type {
  ListStepKey,
  StepOption
} from '../components/providers/create-portcall-provider/step-flow/create-portcall-steps'
import {
  filterStepOptions,
  mergePrincipalOptions,
  toAgencyOptions,
  toCallTypeOptions,
  toClientOptions,
  toPortOptions,
  toVesselOptions
} from '../model/portcall-step-options'

/** Reactive option list + search binding for one list step. */
export interface StepListSource {
  /** The options to render (already filtered for client-side lists). */
  options: ComputedRef<StepOption[]>
  /** The search term — two-way bound to the step's search box. */
  query: Ref<string>
  /** Whether a fetch/search is in flight. */
  loading: Ref<boolean>
}

/**
 * Option sources for the create-portcall step flow's list steps, each backed
 * by the same reference-data composables the classic pickers use:
 *
 * - **agency** / **call type** / **port** — full lists fetched once, filtered
 *   client-side as the user types.
 * - **principal** / **vessel** — debounced server search (the masters are too
 *   large to list eagerly), so the search box drives the request directly.
 *
 * The port list is agency-scoped, so pass a getter for the currently selected
 * agency id; it refetches whenever that changes.
 */
export function usePortcallStepSources(agencyId: () => string) {
  // --- Agency (full list, client filter) ---
  const { agencies, loadingAgencies, fetchAgencies } = useAgencyRefDataSource()
  fetchAgencies()
  const agencyQuery = ref('')
  const agencyOptions = computed<StepOption[]>(() =>
    filterStepOptions(toAgencyOptions(agencies.value ?? []), agencyQuery.value)
  )

  // --- Principal (server search) ---
  const {
    searchInputValue: principalQuery,
    clients,
    isSearching: principalLoading
  } = useClientSearchDataSource()
  // Clients created inline via the "Add client" affordance. The debounced
  // server search won't return a just-created client, so pin them ahead of the
  // results to keep the new (and now selected) principal visible in the list.
  const createdPrincipals = ref<StepOption[]>([])
  function addCreatedPrincipal(option: StepOption) {
    if (!createdPrincipals.value.some((o) => o.value === option.value)) {
      createdPrincipals.value = [option, ...createdPrincipals.value]
    }
  }
  const principalOptions = computed<StepOption[]>(() =>
    mergePrincipalOptions(
      createdPrincipals.value,
      toClientOptions(clients.value)
    )
  )

  // --- Vessel (server search, keyed by IMO) ---
  const {
    searchInputValue: vesselQuery,
    vessels,
    isSearching: vesselLoading
  } = useVesselSearchDataSource()
  const vesselOptions = computed<StepOption[]>(() =>
    toVesselOptions(vessels.value)
  )

  // --- Port-call type (full list, client filter) ---
  const { portCallTypes, loadingPortCallTypes, fetchPortCallTypes } =
    useSelectPortCallTypeDataSource()
  fetchPortCallTypes()
  const callTypeQuery = ref('')
  const callTypeOptions = computed<StepOption[]>(() =>
    filterStepOptions(
      toCallTypeOptions(portCallTypes.value ?? []),
      callTypeQuery.value
    )
  )

  // --- Port of call (agency-scoped list, client filter) ---
  const { ports, loadingPorts, fetchPorts } = useSelectAgencyPortsDataSource()
  watch(agencyId, (id) => fetchPorts(id ?? ''), { immediate: true })
  const portQuery = ref('')
  const portOptions = computed<StepOption[]>(() =>
    filterStepOptions(toPortOptions(ports.value ?? []), portQuery.value)
  )

  const sources: Record<ListStepKey, StepListSource> = {
    agencyId: {
      options: agencyOptions,
      query: agencyQuery,
      loading: loadingAgencies
    },
    appointingPrincipal: {
      options: principalOptions,
      query: principalQuery,
      loading: principalLoading
    },
    vesselImo: {
      options: vesselOptions,
      query: vesselQuery,
      loading: vesselLoading
    },
    callType: {
      options: callTypeOptions,
      query: callTypeQuery,
      loading: loadingPortCallTypes
    },
    portOfCallLocationCode: {
      options: portOptions,
      query: portQuery,
      loading: loadingPorts
    }
  }

  return {
    sourceFor: (key: ListStepKey): StepListSource => sources[key],
    addCreatedPrincipal,
    /** Raw served ports for the current agency (drives the port-of-call reset). */
    agencyPorts: ports
  }
}
